/**
 * 🚩 Wingman Feature Flag Service
 * Centralized control for toggling core intelligence and media systems.
 */

export interface WingmanFeatureFlags {
    aiSummaries: boolean;
    videoEngine: boolean;
    vault: boolean;
    analytics: boolean;
    debugOverlay: boolean;
}

const DEFAULT_FLAGS: WingmanFeatureFlags = {
    aiSummaries: true,
    videoEngine: true,
    vault: true,
    analytics: true,
    debugOverlay: false
};

class FeatureFlagService {
    private static instance: FeatureFlagService;
    private flags: WingmanFeatureFlags = { ...DEFAULT_FLAGS };

    private constructor() {
        this.loadFromStorage();
    }

    public static getInstance(): FeatureFlagService {
        if (!FeatureFlagService.instance) {
            FeatureFlagService.instance = new FeatureFlagService();
        }
        return FeatureFlagService.instance;
    }

    private loadFromStorage() {
        if (typeof window === 'undefined') return;
        const stored = localStorage.getItem('wingman-feature-flags');
        if (stored) {
            try {
                this.flags = { ...DEFAULT_FLAGS, ...JSON.parse(stored) };
            } catch (e) {
                console.warn('[FeatureFlags] Failed to parse stored flags');
            }
        }
    }

    public getFlags(): WingmanFeatureFlags {
        return { ...this.flags };
    }

    public isEnabled(feature: keyof WingmanFeatureFlags): boolean {
        return this.flags[feature] === true;
    }

    public setFlag(feature: keyof WingmanFeatureFlags, enabled: boolean) {
        this.flags[feature] = enabled;
        if (typeof window !== 'undefined') {
            localStorage.setItem('wingman-feature-flags', JSON.stringify(this.flags));
            window.dispatchEvent(new CustomEvent('WINGMAN_FLAGS_UPDATED', { detail: this.flags }));
        }
    }
}

export const featureFlags = FeatureFlagService.getInstance();
